import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Building2,
  CheckCircle2,
  ImagePlus,
  Loader2,
  Plus,
  QrCode,
  Star,
  ToggleLeft,
  ToggleRight,
  X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const EMPTY_QR = {
  label: '', holder_name: '', phone_number: '', qr_image_data: '', is_primary: false,
};
const EMPTY_BANK = {
  label: '', holder_name: '', bank_name: '', currency: 'PEN', account_number: '', cci: '', is_primary: false,
};

function authConfig() {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

function MethodCard({ method, onToggle, onPrimary, busy }) {
  const isQr = method.method_type === 'qr';
  return (
    <article className={`rounded-xl border p-4 ${method.is_active ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-70'}`}>
      <div className="flex items-start gap-3">
        {isQr ? (
          <img src={method.qr_image_url} alt={`QR ${method.label || method.holder_name}`} className="h-20 w-20 flex-none rounded-lg border border-slate-200 object-cover" />
        ) : (
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-lg bg-blue-50 text-blue-700"><Building2 className="h-6 w-6" /></div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-bold text-slate-900">{method.label || (isQr ? method.phone_number : method.bank_name)}</h4>
            {method.is_primary && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800"><Star className="h-3 w-3 fill-current" /> Principal</span>}
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${method.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>{method.is_active ? 'Activo' : 'Inactivo'}</span>
          </div>
          <p className="mt-1 text-sm text-slate-600">Titular: {method.holder_name}</p>
          {isQr ? <p className="text-sm text-slate-600">Celular: {method.phone_number}</p> : (
            <div className="mt-1 space-y-0.5 text-sm text-slate-600">
              <p>{method.bank_name} · {method.currency}</p>
              <p className="break-all">Cuenta: {method.account_number}</p>
              <p className="break-all">CCI: {method.cci}</p>
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
        {method.is_active && !method.is_primary && (
          <button type="button" onClick={() => onPrimary(method)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 px-3 py-2 text-sm font-bold text-amber-800 hover:bg-amber-50 disabled:opacity-50"><Star className="h-4 w-4" /> Hacer principal</button>
        )}
        <button type="button" onClick={() => onToggle(method)} disabled={busy} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-bold disabled:opacity-50 ${method.is_active ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}>
          {method.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}{method.is_active ? 'Desactivar' : 'Activar'}
        </button>
      </div>
    </article>
  );
}

export default function PaymentMethodsModal({ open, onClose }) {
  const [activeTab, setActiveTab] = useState('qr');
  const [methods, setMethods] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [qrForm, setQrForm] = useState(EMPTY_QR);
  const [bankForm, setBankForm] = useState(EMPTY_BANK);
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const fetchMethods = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const response = await axios.get(`${API_URL}/api/admin/payment-methods`, authConfig());
      setMethods(response.data.methods || []);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar los metodos de pago.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) fetchMethods(); }, [open, fetchMethods]);
  useEffect(() => { if (!open) { setFormOpen(false); setMessage(''); setError(''); } }, [open]);

  const visibleMethods = useMemo(() => methods.filter((method) => method.method_type === activeTab), [methods, activeTab]);
  if (!open) return null;

  const resetForm = () => { setQrForm(EMPTY_QR); setBankForm(EMPTY_BANK); setPreview(''); setFormOpen(false); };
  const handleImage = (event) => {
    const file = event.target.files?.[0]; setError('');
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) { setError('Selecciona una imagen PNG, JPG o WEBP.'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('La imagen QR debe pesar menos de 5 MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => { setPreview(reader.result); setQrForm((current) => ({ ...current, qr_image_data: reader.result })); };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault(); setSaving(true); setError(''); setMessage('');
    try {
      const payload = activeTab === 'qr' ? { method_type: 'qr', ...qrForm } : { method_type: 'bank', ...bankForm };
      await axios.post(`${API_URL}/api/admin/payment-methods`, payload, authConfig());
      setMessage(activeTab === 'qr' ? 'QR agregado correctamente.' : 'Cuenta agregada correctamente.');
      resetForm(); await fetchMethods();
    } catch (err) { setError(err.response?.data?.error || 'No se pudo guardar el metodo de pago.'); }
    finally { setSaving(false); }
  };

  const updateMethod = async (method, changes, successMessage) => {
    setBusyId(method.id); setError(''); setMessage('');
    try {
      await axios.patch(`${API_URL}/api/admin/payment-methods/${method.id}`, changes, authConfig());
      setMessage(successMessage); await fetchMethods();
    } catch (err) { setError(err.response?.data?.error || 'No se pudo actualizar el metodo de pago.'); }
    finally { setBusyId(null); }
  };

  const changeTab = (tab) => { setActiveTab(tab); setFormOpen(false); setError(''); setMessage(''); };

  return (
    <div className="fixed inset-0 z-[2100] flex items-center justify-center p-3 sm:p-6">
      <button className="absolute inset-0 bg-slate-950/60" onClick={onClose} aria-label="Cerrar" />
      <div className="relative flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="flex items-start justify-between border-b border-slate-200 px-5 py-4 sm:px-6">
          <div><h2 className="flex items-center gap-2 text-xl font-black text-slate-900"><QrCode className="h-6 w-6 text-brand-primary" /> Metodos de pago Fast Max</h2><p className="mt-1 text-sm text-slate-500">Opciones globales visibles para todos los conductores.</p></div>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Cerrar modal"><X className="h-5 w-5" /></button>
        </header>
        <div className="flex border-b border-slate-200 px-5 sm:px-6">
          {[{ id: 'qr', label: 'Codigos QR', icon: QrCode }, { id: 'bank', label: 'Cuentas bancarias', icon: Building2 }].map((tab) => {
            const Icon = tab.icon;
            return <button key={tab.id} type="button" onClick={() => changeTab(tab.id)} className={`flex flex-1 items-center justify-center gap-2 border-b-2 px-3 py-3 text-sm font-bold ${activeTab === tab.id ? 'border-brand-primary text-brand-primary' : 'border-transparent text-slate-500 hover:text-slate-800'}`}><Icon className="h-4 w-4" /> {tab.label}</button>;
          })}
        </div>
        <main className="overflow-y-auto p-5 sm:p-6">
          {(error || message) && <div className={`mb-4 rounded-lg px-4 py-3 text-sm font-semibold ${error ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>{error || message}</div>}
          <div className="mb-4 flex items-center justify-between gap-3">
            <div><h3 className="font-bold text-slate-900">{activeTab === 'qr' ? 'Codigos QR' : 'Cuentas bancarias'}</h3><p className="text-xs text-slate-500">Solo los metodos activos aparecen al conductor.</p></div>
            <button type="button" onClick={() => setFormOpen((value) => !value)} className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-2 text-sm font-bold text-white hover:bg-brand-primary/90">{formOpen ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}{formOpen ? 'Cancelar' : activeTab === 'qr' ? 'Agregar QR' : 'Agregar cuenta'}</button>
          </div>

          {formOpen && (
            <form onSubmit={handleSubmit} className="mb-5 rounded-xl border border-brand-primary/20 bg-brand-primary/5 p-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="text-sm font-semibold text-slate-700">Etiqueta <span className="font-normal text-slate-400">(opcional)</span>
                  <input value={activeTab === 'qr' ? qrForm.label : bankForm.label} onChange={(event) => activeTab === 'qr' ? setQrForm({ ...qrForm, label: event.target.value }) : setBankForm({ ...bankForm, label: event.target.value })} placeholder={activeTab === 'qr' ? 'Ej. QR principal' : 'Ej. Cuenta BCP soles'} maxLength={120} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 outline-none focus:border-brand-primary" />
                </label>
                <label className="text-sm font-semibold text-slate-700">Titular
                  <input required value={activeTab === 'qr' ? qrForm.holder_name : bankForm.holder_name} onChange={(event) => activeTab === 'qr' ? setQrForm({ ...qrForm, holder_name: event.target.value }) : setBankForm({ ...bankForm, holder_name: event.target.value })} maxLength={160} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 outline-none focus:border-brand-primary" />
                </label>
                {activeTab === 'qr' ? <>
                  <label className="text-sm font-semibold text-slate-700">Numero de celular<input required type="tel" value={qrForm.phone_number} onChange={(event) => setQrForm({ ...qrForm, phone_number: event.target.value })} maxLength={30} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 outline-none focus:border-brand-primary" /></label>
                  <label className="text-sm font-semibold text-slate-700">Imagen QR
                    <span className="mt-1 flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white p-3 hover:border-brand-primary">{preview ? <img src={preview} alt="Vista previa QR" className="h-16 w-16 rounded object-cover" /> : <ImagePlus className="h-7 w-7 text-slate-400" />}<span className="text-xs font-medium text-slate-500">PNG, JPG o WEBP · max. 5 MB</span></span>
                    <input required type="file" accept="image/png,image/jpeg,image/webp" onChange={handleImage} className="sr-only" />
                  </label>
                </> : <>
                  <label className="text-sm font-semibold text-slate-700">Banco<input required value={bankForm.bank_name} onChange={(event) => setBankForm({ ...bankForm, bank_name: event.target.value })} maxLength={120} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 outline-none focus:border-brand-primary" /></label>
                  <label className="text-sm font-semibold text-slate-700">Moneda<select value={bankForm.currency} onChange={(event) => setBankForm({ ...bankForm, currency: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 outline-none focus:border-brand-primary"><option value="PEN">PEN · Soles</option><option value="USD">USD · Dolares</option></select></label>
                  <label className="text-sm font-semibold text-slate-700">Numero de cuenta<input required value={bankForm.account_number} onChange={(event) => setBankForm({ ...bankForm, account_number: event.target.value })} maxLength={80} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 outline-none focus:border-brand-primary" /></label>
                  <label className="text-sm font-semibold text-slate-700">CCI<input required value={bankForm.cci} onChange={(event) => setBankForm({ ...bankForm, cci: event.target.value })} maxLength={80} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 outline-none focus:border-brand-primary" /></label>
                </>}
              </div>
              <label className="mt-4 flex items-center gap-2 text-sm font-semibold text-slate-700"><input type="checkbox" checked={activeTab === 'qr' ? qrForm.is_primary : bankForm.is_primary} onChange={(event) => activeTab === 'qr' ? setQrForm({ ...qrForm, is_primary: event.target.checked }) : setBankForm({ ...bankForm, is_primary: event.target.checked })} className="h-4 w-4 accent-brand-primary" />Definir como principal</label>
              <div className="mt-4 flex justify-end"><button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-brand-green px-5 py-2.5 font-bold text-white disabled:opacity-50">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}Guardar</button></div>
            </form>
          )}

          {loading ? <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-brand-primary" /></div> : visibleMethods.length ? (
            <div className="grid gap-3 md:grid-cols-2">{visibleMethods.map((method) => <MethodCard key={method.id} method={method} busy={busyId !== null} onToggle={(item) => updateMethod(item, { is_active: !item.is_active }, item.is_active ? 'Metodo desactivado.' : 'Metodo activado.')} onPrimary={(item) => updateMethod(item, { is_primary: true }, 'Metodo principal actualizado.')} />)}</div>
          ) : <div className="rounded-xl border border-dashed border-slate-300 px-5 py-12 text-center text-slate-500">{activeTab === 'qr' ? <QrCode className="mx-auto mb-3 h-9 w-9" /> : <Building2 className="mx-auto mb-3 h-9 w-9" />}<p className="font-semibold">Aun no hay metodos configurados.</p></div>}
        </main>
      </div>
    </div>
  );
}
